import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Info, Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { cn, formatarDataHora } from '@/lib/utils';
import type { ModeloEmail, PreviaEmail, VariavelModelo } from '@/features/notificacoes/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { AreaTexto, Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

export function PaginaModelosEmail() {
  const queryClient = useQueryClient();
  const [chaveAtiva, setChaveAtiva] = React.useState<string | null>(null);

  const { data: modelos, isPending } = useQuery({
    queryKey: ['modelos-email'],
    queryFn: () => api.get<ModeloEmail[]>('/modelos-email'),
  });

  const ativo = modelos?.find((m) => m.chave === chaveAtiva) ?? modelos?.[0];

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['modelos-email'] });

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-80" />
        <Esqueleto className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div>
      <CabecalhoPagina
        titulo="Modelos de e-mail"
        descricao="O texto das mensagens que o sistema envia. O layout institucional é aplicado automaticamente."
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">Escreva o texto; as variáveis o sistema preenche.</p>
          <p className="mt-0.5">
            Tudo entre chaves duplas é substituído no momento do envio. Uma variável fora da lista
            impede salvar — ela chegaria ao paciente como <code>{'{{texto}}'}</code> no meio da
            mensagem.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Modelos disponíveis">
        {modelos?.map((modelo) => (
          <button
            key={modelo.chave}
            type="button"
            role="tab"
            aria-selected={modelo.chave === ativo?.chave}
            onClick={() => setChaveAtiva(modelo.chave)}
            className={cn(
              'focus-visible:outline-foco rounded-md border px-3 py-1.5 text-sm focus-visible:outline-2 focus-visible:outline-offset-2',
              modelo.chave === ativo?.chave
                ? 'border-marca bg-marca text-marca-contraste'
                : 'border-borda bg-cartao hover:bg-silenciado',
            )}
          >
            {modelo.rotulo}
            {modelo.personalizado && <span className="ml-1.5 text-xs opacity-70">· editado</span>}
          </button>
        ))}
      </div>

      {ativo && <Editor key={ativo.chave} modelo={ativo} aoSalvar={invalidar} />}
    </div>
  );
}

interface EditorProps {
  modelo: ModeloEmail;
  aoSalvar: () => Promise<void>;
}

function Editor({ modelo, aoSalvar }: EditorProps) {
  const [assunto, setAssunto] = React.useState(modelo.assunto);
  const [corpo, setCorpo] = React.useState(modelo.corpo);
  const [previa, setPrevia] = React.useState<PreviaEmail | null>(null);
  const [confirmandoRestauro, setConfirmandoRestauro] = React.useState(false);

  const areaCorpo = React.useRef<HTMLTextAreaElement>(null);

  const alterado = assunto !== modelo.assunto || corpo !== modelo.corpo;

  const salvar = useMutation({
    mutationFn: () => api.patch(`/modelos-email/${modelo.chave}`, { assunto, corpo }),
    onSuccess: async () => {
      toast.success('Modelo salvo. Os próximos envios já usam este texto.');
      await aoSalvar();
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.'),
  });

  const restaurar = useMutation({
    mutationFn: () => api.delete(`/modelos-email/${modelo.chave}`),
    onSuccess: async () => {
      setAssunto(modelo.assuntoPadrao);
      setCorpo(modelo.corpoPadrao);
      setConfirmandoRestauro(false);
      toast.success('Modelo restaurado ao texto padrão.');
      await aoSalvar();
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível restaurar.'),
  });

  const visualizar = useMutation({
    mutationFn: () =>
      api.post<PreviaEmail>(`/modelos-email/${modelo.chave}/previa`, { assunto, corpo }),
    onSuccess: (r) => setPrevia(r),
    onError: (erro) =>
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível pré-visualizar.',
      ),
  });

  /** Insere a variável onde está o cursor, em vez de obrigar a digitá-la. */
  const inserir = (nome: string) => {
    const area = areaCorpo.current;
    if (!area) return;

    const marca = `{{${nome}}}`;
    const inicio = area.selectionStart;
    const fim = area.selectionEnd;

    setCorpo(`${corpo.slice(0, inicio)}${marca}${corpo.slice(fim)}`);

    // O cursor precisa ir para depois da marcação, ou a próxima inserção
    // cairia no meio da anterior.
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(inicio + marca.length, inicio + marca.length);
    });
  };

  const usadas = new Set(
    [...`${assunto}\n${corpo}`.matchAll(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g)].map(
      (c) => c[1],
    ),
  );

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo>{modelo.rotulo}</CartaoTitulo>
          <CartaoDescricao>
            {modelo.descricao}
            {modelo.personalizado && modelo.atualizadoEm && (
              <span className="mt-1 block text-xs">
                Editado em {formatarDataHora(modelo.atualizadoEm)}
                {modelo.atualizadoPor && ` por ${modelo.atualizadoPor}`}.
              </span>
            )}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <div className="flex flex-col gap-4">
            <Campo id="modelo-assunto" rotulo="Assunto" obrigatorio>
              <Entrada value={assunto} onChange={(e) => setAssunto(e.target.value)} />
            </Campo>

            <Campo
              id="modelo-corpo"
              rotulo="Corpo"
              obrigatorio
              ajuda="Uma linha em branco separa parágrafos. Cabeçalho e rodapé do KAVI são acrescentados no envio."
            >
              <AreaTexto
                ref={areaCorpo}
                rows={18}
                className="font-mono text-[13px]"
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
              />
            </Campo>

            <div className="flex flex-wrap gap-2">
              <Botao
                type="button"
                variante="contorno"
                onClick={() => visualizar.mutate()}
                disabled={visualizar.isPending}
              >
                {visualizar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Eye aria-hidden="true" />
                )}
                Pré-visualizar
              </Botao>

              <Botao
                type="button"
                onClick={() => salvar.mutate()}
                disabled={salvar.isPending || !alterado}
              >
                {salvar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Salvar
              </Botao>

              {modelo.personalizado && (
                <Botao
                  type="button"
                  variante="fantasma"
                  onClick={() => setConfirmandoRestauro(true)}
                >
                  <RotateCcw aria-hidden="true" />
                  Restaurar padrão
                </Botao>
              )}
            </div>
          </div>
        </CartaoConteudo>
      </Cartao>

      <Cartao className="h-fit">
        <CartaoCabecalho>
          <CartaoTitulo className="text-base">Variáveis disponíveis</CartaoTitulo>
          <CartaoDescricao>Clique para inserir onde está o cursor.</CartaoDescricao>
        </CartaoCabecalho>
        <CartaoConteudo>
          <ul className="flex flex-col gap-2">
            {modelo.variaveis.map((variavel) => (
              <ItemVariavel
                key={variavel.nome}
                variavel={variavel}
                usada={usadas.has(variavel.nome)}
                aoInserir={() => inserir(variavel.nome)}
              />
            ))}
          </ul>
        </CartaoConteudo>
      </Cartao>

      <Dialogo open={previa !== null} onOpenChange={(aberto) => !aberto && setPrevia(null)}>
        <DialogoConteudo className="max-w-3xl">
          <DialogoCabecalho>
            <DialogoTitulo>Pré-visualização</DialogoTitulo>
            <DialogoDescricao>
              Com dados fictícios, pelo mesmo caminho do envio de verdade.
            </DialogoDescricao>
          </DialogoCabecalho>

          <DialogoCorpo>
            <p className="mb-3 text-sm">
              <span className="text-silenciado-texto">Assunto: </span>
              <strong>{previa?.assunto}</strong>
            </p>

            {/* srcDoc isola o HTML do e-mail da página: o documento roda em
                outra origem e não alcança nada daqui. */}
            <iframe
              title="Pré-visualização do e-mail"
              srcDoc={previa?.html ?? ''}
              sandbox=""
              className="border-borda h-[420px] w-full rounded-md border bg-white"
            />
          </DialogoCorpo>

          <DialogoRodape>
            <Botao type="button" variante="contorno" onClick={() => setPrevia(null)}>
              Fechar
            </Botao>
          </DialogoRodape>
        </DialogoConteudo>
      </Dialogo>

      <Dialogo open={confirmandoRestauro} onOpenChange={setConfirmandoRestauro}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Restaurar o texto padrão</DialogoTitulo>
            <DialogoDescricao>
              O texto escrito pela instituição será descartado e o modelo volta ao que vem com o
              sistema. Não há como desfazer.
            </DialogoDescricao>
          </DialogoCabecalho>

          <DialogoRodape>
            <Botao type="button" variante="contorno" onClick={() => setConfirmandoRestauro(false)}>
              Cancelar
            </Botao>
            <Botao
              type="button"
              variante="destrutivo"
              onClick={() => restaurar.mutate()}
              disabled={restaurar.isPending}
            >
              {restaurar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              Restaurar
            </Botao>
          </DialogoRodape>
        </DialogoConteudo>
      </Dialogo>
    </div>
  );
}

interface ItemVariavelProps {
  variavel: VariavelModelo;
  usada: boolean;
  aoInserir: () => void;
}

function ItemVariavel({ variavel, usada, aoInserir }: ItemVariavelProps) {
  return (
    <li>
      <button
        type="button"
        onClick={aoInserir}
        className="border-borda hover:bg-silenciado focus-visible:outline-foco w-full rounded-md border p-2 text-left focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <span className="flex flex-wrap items-center gap-1.5">
          <code className="text-marca text-xs">{`{{${variavel.nome}}}`}</code>
          {variavel.bloco && <Etiqueta tom="marca">bloco</Etiqueta>}
          {variavel.obrigatoria && <Etiqueta tom="alerta">obrigatória</Etiqueta>}
          {usada && <Etiqueta tom="sucesso">em uso</Etiqueta>}
        </span>
        <span className="text-silenciado-texto mt-0.5 block text-xs">{variavel.descricao}</span>
      </button>
    </li>
  );
}
